import type { InterviewStageActivityAction } from '@/context-providers/interviewStageManager';

export const CLONE_NAME_PREFIX = 'Copy of';

type MaybeAction = InterviewStageActivityAction | null | undefined;

export type CloneableActivity = {
  id: number | string;
  kind: string;
  label?: string;
  [key: string]: unknown;
};
export type CloneableStage = {
  id: number | string;
  name?: string;
  position?: number;
  ats_job_stage_actions?: Array<Partial<CloneableActivity> | null | undefined>;
  [key: string]: unknown;
};
export type CloneSection = {
  stage: CloneableStage;
  activities: CloneableActivity[];
};

export const getCloneName = (name?: string | null): string => {
  const trimmed = (name ?? '').trim();
  return trimmed ? `${CLONE_NAME_PREFIX} ${trimmed}` : CLONE_NAME_PREFIX;
};

export const isCloneAction = (action?: MaybeAction): boolean =>
  action?.clone?.activity?.id !== undefined &&
  action?.clone?.activity?.id !== null;

/** Where a form loads its prefill from: the clone source, else the activity being edited. */
export const getActivityLoadTarget = (action?: MaybeAction) => ({
  activityId: action?.clone?.activity?.id ?? action?.activity?.id,
  stageId: action?.clone?.stage?.id ?? action?.stage?.id,
});

/** Extra create params that tell the backend which activity to copy files from and place under. */
export const getCloneSourceParams = (
  action?: MaybeAction
): { source_ats_job_stage_action_id?: number | string } =>
  isCloneAction(action)
    ? { source_ats_job_stage_action_id: action?.clone?.activity.id }
    : {};

/**
 * The loaded activity with its name turned into "Copy of …" when cloning; the
 * same object otherwise.
 *
 * The source `id` is intentionally KEPT (not stripped): forms key their
 * prefill off `data?.id` (e.g. deciding whether they're loading vs. blank),
 * and clearing it here would make a clone look like an unloaded form. The
 * backend treats this as a create either way — `getCloneSourceParams` is
 * what tells it which activity to copy from, not this `id`.
 *
 * A clone also drops the source's lifecycle `status`: a new activity has not
 * been sent or scheduled yet, and several forms (survey's `SurveyDetails`,
 * the coding-assessment test picker) read `activity?.status` to lock fields
 * once it has — a clone of a sent/scheduled source must not inherit that.
 */
export const withCloneName = <T>(data: T, action?: MaybeAction): T => {
  if (!data || typeof data !== 'object' || !isCloneAction(action)) return data;
  const {
    label,
    status: _status,
    ...rest
  } = data as {
    label?: unknown;
    status?: unknown;
  };
  return {
    ...rest,
    label: getCloneName(typeof label === 'string' ? label : ''),
  } as T;
};

/** The plan's activities grouped under their stage, in plan order, limited to kinds the user may add. */
export const getCloneSections = (
  stages: CloneableStage[] | undefined,
  allowedKinds: ReadonlySet<string>
): CloneSection[] =>
  [...(stages ?? [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((stage) => ({
      stage,
      activities: (stage.ats_job_stage_actions ?? []).filter(
        (activity): activity is CloneableActivity =>
          activity?.id !== undefined &&
          activity?.id !== null &&
          typeof activity?.kind === 'string' &&
          allowedKinds.has(activity.kind)
      ),
    }))
    .filter((section) => section.activities.length > 0);
